import React, {memo} from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import {StaticImageData} from 'next/image';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import {fadeInUp} from '@/animation/scroll-animation';
import dynamic from 'next/dynamic';
import {borderRadius, colors, fontSize, fontWeight} from '@/styles/tokens';

const AnimatedComponent = dynamic(() => import('../AnimatedComponent').then((mod) => mod.AnimatedComponent));

interface Framework {
    label: string;
}

interface MilestonesProps {
    heading?: string;
    milestones: {value: string; text: string}[];
    frameworksHeading?: string;
    frameworks?: Framework[];
    imageSrc?: string | StaticImageData;
    imageAlt?: string;
}

const milestoneItemBoxSx = {
    textAlign: {xs: 'center', md: 'left'},
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    color: '#000'
} as const;

const milestoneValueSx = {
    fontWeight: fontWeight.extrabold,
    color: '#111 !important',
    lineHeight: 1.2,
    display: 'inline-block',
    transition: 'transform .1s ease-out',
    transformOrigin: 'left bottom',
    willChange: 'transform',
    '&:hover': {
        transform: 'scale(1.3)'
    }
} as const;

const milestoneTextSx = {
    mt: 1.5,
    lineHeight: 1.4,
    whiteSpace: 'pre-line',
    fontSize: fontSize[16],
    color: colors.text.primary
} as const;

const ShieldCheckIcon = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{flexShrink: 0}}
    >
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        <polyline points="9 12 11 14 15 10" />
    </svg>
);

const MilestonesCrypto = ({heading, milestones, frameworksHeading, frameworks}: MilestonesProps) => {
    return (
        <Box>
            <MUIContainer>
                {/* Optional heading */}
                {heading && (
                    <AnimatedComponent variants={fadeInUp}>
                        <Typography
                            variant="h2"
                            component="h2"
                            className="font-headers"
                            sx={{fontWeight: 900, color: '#111', mb: {xs: 4, md: 5}, fontSize: {xs: '30px', md: '44px'}}}
                        >
                            {heading}
                        </Typography>
                    </AnimatedComponent>
                )}

                {/* Milestones row */}
                <Grid alignItems="center" justifyContent="center">
                    <Grid item xs={12}>
                        <Grid container spacing={{xs: 4, md: 3}} justifyContent="flex-start">
                            {milestones?.map((item, idx) => (
                                <Grid item xs={12} sm={6} md={2.4} key={idx}>
                                    <AnimatedComponent variants={fadeInUp} delay={idx * 0.1}>
                                        <Box sx={milestoneItemBoxSx}>
                                            <Typography
                                                variant="h4"
                                                component="p"
                                                className="custom-heading-gradient-color"
                                                sx={milestoneValueSx}
                                            >
                                                {item.value}
                                            </Typography>
                                            <Typography className="font-regular-text" sx={milestoneTextSx}>
                                                {item.text}
                                            </Typography>
                                        </Box>
                                    </AnimatedComponent>
                                </Grid>
                            ))}
                        </Grid>
                    </Grid>
                </Grid>

                {/* Optional regulatory frameworks */}
                {frameworks && frameworks.length > 0 && (
                    <Box sx={{mt: {xs: 3, md: 5}}}>
                        {frameworksHeading && (
                            <AnimatedComponent variants={fadeInUp}>
                                <Typography
                                    variant="h4"
                                    component="h2"
                                    className="font-headers"
                                    sx={{fontWeight: 900, color: '#111', mb: {xs: 3, md: 4}, fontSize: {xs: '30px', md: '36px'}}}
                                >
                                    {frameworksHeading}
                                </Typography>
                            </AnimatedComponent>
                        )}
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 4,
                                justifyContent: {xs: 'center', md: 'flex-start'}
                            }}
                        >
                            {frameworks?.map((fw, idx) => (
                                <AnimatedComponent key={idx} variants={fadeInUp} delay={idx * 0.08}>
                                    <Box
                                        sx={{
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: 1,
                                            borderRadius: borderRadius.lg,
                                            backgroundColor: '#eef0fb',
                                            py: 1.8,
                                            px: 4,
                                            color: colors.primary.main,
                                            transition: 'background-color 0.2s ease',
                                            className: 'font-headers'
                                        }}
                                    >
                                        <ShieldCheckIcon />
                                        <Typography
                                            className="font-headers"
                                            sx={{fontWeight: fontWeight.semibold, color: 'inherit', fontSize: '16px'}}
                                        >
                                            {fw.label}
                                        </Typography>
                                    </Box>
                                </AnimatedComponent>
                            ))}
                        </Box>
                    </Box>
                )}
            </MUIContainer>
        </Box>
    );
};

export default memo(MilestonesCrypto);
